import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Tooltip } from '@mantine/core';
import { FaArrowLeftLong, FaArrowRightLong } from 'react-icons/fa6';
import {
  useWorkflowStages,
  type WorkflowStage,
  type UseWorkflowStagesArgs
} from '../../../hooks/useWorkflowStages';
import {
  useAutomateExtraction,
  useAutomateTransformation,
  useCopyJurisdictions
} from '../../../services/automation.service';
import { extractApiError } from '../../../utils';
import { useWorkflowChrome } from '../WorkflowChromeContext';
import StepItem from './StepItem';
import { ArrowDivider, BackButton, Connector, StepItemWrap, StepperBar, StepsRow } from './style';

export interface WorkflowStepperProps extends UseWorkflowStagesArgs {
  onBack?: () => void;
}

const isDoneOrCurrent = (stage: WorkflowStage) =>
  stage.status === 'done' || stage.status === 'current';

const WorkflowStepper: React.FC<WorkflowStepperProps> = ({ onBack, ...propArgs }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useWorkflowChrome();
  const args = {
    workflowId: propArgs.workflowId ?? ctx.workflowId,
    periodId: propArgs.periodId ?? ctx.periodId
  };
  const { stages, currentStageIndex } = useWorkflowStages(args);
  // Shortcut-action hooks MUST live above the early return below — otherwise
  // the hook count changes between renders (violates the Rules of Hooks and
  // triggers "Rendered more hooks than during the previous render").
  const queryClient = useQueryClient();
  const copyJurisdictions = useCopyJurisdictions();
  const automateExtraction = useAutomateExtraction();
  const automateTransformation = useAutomateTransformation();

  // Hide entirely on pages that didn't register a periodId — keeps the stepper
  // off projects-list, dashboard, etc.
  if (!args.periodId) return null;

  // Active = which stage's PAGE the user is on (URL-driven via context).
  // Falls back to the workflow-level "current" stage if the page didn't set it.
  const activeIndex = ctx.activeStageKey ? stages.findIndex(s => s.key === ctx.activeStageKey) : -1;
  const cursorIndex = activeIndex >= 0 ? activeIndex : currentStageIndex;

  const prevStage = cursorIndex > 0 ? stages[cursorIndex - 1] : undefined;
  const nextStage =
    cursorIndex >= 0 && cursorIndex < stages.length - 1 ? stages[cursorIndex + 1] : undefined;

  const goToStage = (stage?: WorkflowStage) => {
    if (!stage || !stage.path || stage.path.startsWith('#')) return;

    const [destPath, destSearch = ''] = stage.path.split('?');

    // Same-page navigation (e.g. Extraction → Transformation both live on
    // /data-extraction/:wf/:p): merge current query params with the
    // destination's so per-page state (fileId, sheetId, category,
    // extractId, …) survives. Destination params win on conflict.
    if (destPath === location.pathname && (location.search || destSearch)) {
      const merged = new URLSearchParams(location.search);
      new URLSearchParams(destSearch).forEach((value, key) => merged.set(key, value));
      const mergedString = merged.toString();
      navigate(mergedString ? `${destPath}?${mergedString}` : destPath);
      return;
    }

    // Different-page navigation (e.g. Extraction → Source Files / AIFs):
    // navigate to exactly `stage.path` as a single URL string. React Router
    // replaces both pathname and search on the new history entry — current
    // location.search is dropped.
    navigate(stage.path);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (ctx.backStepHandler) {
      ctx.backStepHandler();
      return;
    }
    if (prevStage) {
      goToStage(prevStage);
      return;
    }
    // No previous stage and no override — exit the workflow to the project's
    // Quarters dashboard. `navigate(-1)` was unreliable when the user landed
    // on the first stage via a deep link (empty history → did nothing).
    if (args.workflowId) {
      navigate(`/project/quaters/${args.workflowId}`);
      return;
    }
    navigate(-1);
  };

  const handleNext = () => {
    if (ctx.nextStepHandler) {
      ctx.nextStepHandler();
      return;
    }
    goToStage(nextStage);
  };

  const handleStageClick = (stage: WorkflowStage) => {
    // `upcoming` stages still navigate — destination pages handle their own
    // empty/disabled states. Blocking them caused "link doesn't work" for
    // stages that share a page with the current one (Source Files ↔ AIFs).
    goToStage(stage);
  };

  const backDisabled = !onBack && !ctx.backStepHandler && !prevStage;
  // Per-stage "one-click shortcut" dispatchers. When more stage kinds land
  // (copy AIFs, automate extraction, automate transformation), add another
  // branch here — the stepper is the single owner of shortcut orchestration
  // so `useWorkflowStages` stays pure metadata. The `useQueryClient` and
  // `useCopyJurisdictions` hooks themselves are declared above the early
  // return so the hook order stays stable across renders.
  const handleActionClick = (stage: WorkflowStage) => {
    if (!stage.action || !args.periodId) return;
    // Mutations are untyped JS services — TS infers `void` for their
    // parameter, so annotate the payload as `any` at the call site
    // instead of casting at every `.mutate(...)` invocation.
    const payload: any = { period_id: args.periodId };
    // Every shortcut nav-target = the stage the shortcut is attached to.
    // After the mutation kicks off, drop the user on that page so they can
    // watch progress (Issues drawer, extraction status, etc.) live instead
    // of being stuck on the previous page with a stale sub-label.
    const onSuccess = (message: string, options?: { extraParams?: string }) => () => {
      queryClient.invalidateQueries();
      toast.success(message, { position: 'bottom-center' });
      // Append optional extra params (e.g. `openIssues=true`) so the
      // destination page can react to the shortcut having just fired.
      if (options?.extraParams) {
        const [path, existingSearch = ''] = stage.path.split('?');
        const merged = new URLSearchParams(existingSearch);
        new URLSearchParams(options.extraParams).forEach((v, k) => merged.set(k, v));
        navigate(`${path}?${merged.toString()}`);
      } else {
        goToStage(stage);
      }
    };
    const onError = (fallback: string) => (err: unknown) => {
      toast.error(extractApiError(err, fallback));
    };
    if (stage.action.kind === 'copy-jurisdictions') {
      copyJurisdictions.mutate(payload, {
        onSuccess: onSuccess('Jurisdictions copied from previous period'),
        onError: onError('Could not copy jurisdictions from previous period')
      });
    } else if (stage.action.kind === 'automate-extraction') {
      automateExtraction.mutate(payload, {
        onSuccess: onSuccess('Automated extraction has been started', {
          extraParams: 'openIssues=true'
        }),
        onError: onError('Unable to start automatic extraction.')
      });
    } else if (stage.action.kind === 'automate-transformation') {
      automateTransformation.mutate(payload, {
        onSuccess: onSuccess('Automated transformation has been started', {
          extraParams: 'openIssues=true'
        }),
        onError: onError('Unable to start automatic transformation.')
      });
    }
  };

  const isActionLoadingFor = (stage: WorkflowStage): boolean => {
    if (!stage.action) return false;
    if (stage.action.kind === 'copy-jurisdictions') return copyJurisdictions.isLoading;
    if (stage.action.kind === 'automate-extraction') return automateExtraction.isLoading;
    if (stage.action.kind === 'automate-transformation') return automateTransformation.isLoading;
    return false;
  };

  // The next stage is "not ready" when `useWorkflowStages` returned its path
  // as `#` (workflow status hasn't unlocked it yet — e.g. Report from Output
  // Tables before the report has been generated). This only matters for the
  // default `goToStage(nextStage)` path — when a page registers an explicit
  // `nextStepHandler` (e.g. OutputTables' onNext, which generates the report
  // before navigating), the page owns "can next happen", so skip the lock.
  const nextStageLocked = !!nextStage && (!nextStage.path || nextStage.path.startsWith('#'));
  const isLockedWithoutHandler = nextStageLocked && !ctx.nextStepHandler;
  const nextDisabled = !nextStage || isLockedWithoutHandler || ctx.nextStepDisabled === true;
  const backTooltip = prevStage ? `Back to ${prevStage.label}` : 'Back';
  // When the active page wants the tooltip to advertise a different
  // destination than the strict-next stage (because its `nextStepHandler`
  // jumps further), it can set `ctx.nextStepLabel`. Falls back to the
  // computed next stage's label.
  const nextLabel = ctx.nextStepLabel ?? nextStage?.label;
  const nextTooltip = nextStage
    ? ctx.nextStepDisabled || isLockedWithoutHandler
      ? `${nextLabel} not ready yet`
      : `Next: ${nextLabel}`
    : 'No next step';

  return (
    <StepperBar>
      <Tooltip label={backTooltip} withArrow position="bottom">
        <BackButton
          type="button"
          aria-label={backTooltip}
          onClick={handleBack}
          disabled={backDisabled}
        >
          <FaArrowLeftLong size={16} />
        </BackButton>
      </Tooltip>
      <ArrowDivider aria-hidden />
      <StepsRow>
        {stages.map((stage, index) => {
          const next = stages[index + 1];
          const connectorTone =
            next && isDoneOrCurrent(stage) && isDoneOrCurrent(next) ? 'done' : 'pending';
          return (
            <StepItemWrap key={stage.key}>
              <StepItem
                stage={stage}
                index={index}
                onClick={handleStageClick}
                isActive={stage.key === ctx.activeStageKey}
                onActionClick={handleActionClick}
                isActionLoading={isActionLoadingFor(stage)}
              />
              {next && <Connector $tone={connectorTone} aria-hidden />}
            </StepItemWrap>
          );
        })}
      </StepsRow>
      <ArrowDivider aria-hidden />
      <Tooltip label={nextTooltip} withArrow position="bottom">
        <BackButton
          type="button"
          aria-label={nextTooltip}
          onClick={handleNext}
          disabled={nextDisabled}
        >
          <FaArrowRightLong size={16} />
        </BackButton>
      </Tooltip>
    </StepperBar>
  );
};

export default WorkflowStepper;
